import { Meta } from '@storybook/addon-docs/blocks';

<Meta title="Overview/Styling API" />

# Styling API

While the specifics vary by component, there are a variety of methods exposed
that make it possible to alter the look and feel of these components. For more
information on these approaches, consult
[Options for Styling Web Components](https://nolanlawson.com/2021/01/03/options-for-styling-web-components/).

## Styling Hooks In Use

### CSS Custom Properties

With the introduction of [Open Props](https://open-props.style/) as a dependency
to this library, CSS custom properties are the primary approach to global
theming across this design system. Individual components may make use of a
variety of global variables, which can be overriden either at the component
level, or higher in the DOM in order to cascade to all components.

For example, the `gdwc-card` component will set the font-family based on the
`--gdwc-font-family` variable. If you wanted to theme cards to use a monospace
font, you could use the following css:

```css
gdwc-card {
  --gdwc-font-family: monospace;
}
```

Or alternatively, the value could be one of the
[font-family variables provided by Open Props](https://open-props.style/#typography),
which will result in a richer set of fallback fonts.

```css
gdwc-card {
  --gdwc-font-family: var(--font-mono);
}
```

If you are using a variety of GDWC components, you may instead set these
variables higher in the document scope. These variables will then automatically
cascade to all child elements in the DOM.

```css
body {
  --gdwc-font-family: var(--font-mono);
}
```

Going a step further, you may also want to apply the normalize stylesheet from
Open Props globally. This will result in consistent Open Props based theming
being applied to all elements, not just GDWC elements.

```html
<link rel="stylesheet" href="https://unpkg.com/open-props/normalize.min.css" />
```

The following variables may be used by GDWC components:

| Variable                   | Usage                                          | Values                                                             | Required |
| -------------------------- | ---------------------------------------------- | ------------------------------------------------------------------ | -------- |
| --gdwc-font-family         | font-family css property                       | Open Props font family variables, any valid font-family value.     | yes      |
| --gdwc-font-lineheight     | line-height css property                       | Open Props line hight variables, any valid line-height value.      | yes      |
| --gdwc-text-1              | Primary text color (Open Props --text-1)       | Open Props color variables, any valid color value.                 | yes      |
| --gdwc-text-2              | Secondary text color (Open Props --text-2)     | Open Props color variables, any valid color value.                 | yes      |
| --gdwc-link                | Link color (Open Props --link)                 | Open Props color variables, any valid color value.                 | yes      |
| --gdwc-link-visited        | Visited link color (Open Props --link-visited) | Open Props color variables, any valid color value.                 | yes      |
| --gdwc-surface-1           | Primary surface color (Open Props --surface-1) | Open Props color variables, any valid color value.                 | yes      |
| --gdwc-padding             | Element padding                                | Open Props size variables, any valid padding value.                | no       |
| --gdwc-border-radius       | Element border-radius                          | Open Props border radius variables, any valid border radius value. | no       |
| --gdwc-border              | Element border                                 | Any valid border value. Can include other css variables.           | no       |
| --gdwc-background-gradient | CSS background                                 | Open Props gradient variables.                                     | no       |
| --gdwc-shadow              | Element box shadow                             | Open props shadow variables, any valid box-shadow variable.        | no       |

Required variables are used by all GDWC components. Optional variables are only
used by components where applicable.

### Theme Property

It is possible to use the `theme` property to trigger styling using a number of
predefined themes. For example, to render `gdwc-menu` as a horizontal menu use
`theme=horizontal`
[(Live example)](/?path=/story/components-menu--theme-horizontal):

```html
<gdwc-menu
  branding="API Driven Menu"
  baseurl="https://dev-ds-demo.pantheonsite.io"
  menuid="main"
  theme="horizontal"
></gdwc-menu>
```

`gdwc-menu` also supports an `unstyled` theme
[(Live example)](/?path=/story/components-menu--theme-unstyled) that disables
all styling and only provides default markup. All future gdwc components will
most likely support this theme.

Specific themes can be targeted in component styles using an approach similar
to:

```css
:host([theme='horizontal']) slot h2 {
  float: left;
}
```

And styles can be disabled for the `unstyled` theme using an approach similar
to:

```css
:host(:not([theme='unstyled'])) li > ul {
  display: none;
}
```

### Shadow Parts

Explicitly defined css parts that can be styled outside of the shadow DOM.

Example: `menu-level` and `menu-item` parts defined in `gdwc-menu`
[(Live example)](/?path=/story/components-menu--styled-by-shadow-parts)

Outside of the component, target these parts with css like:

```css
gdwc-menu#::part(menu-level) {
  border: 5px dotted red;
  background: hotpink;
}
gdwc-menu::part(menu-item) {
  font-size: 20px;
  list-style: none;
  background: aliceblue;
}
```

### Slots

A placeholder that can be filled with markup from outside of the shadow DOM.

Example: `brand` slot defined in `gdwc-menu`
[(Live example)](/?path=/story/components-menu--brand-slot-override)

```html
<style>
  h1 {
    display: inline;
    padding: 0.5rem;
    background-color: hotpink;
    border: 5px solid black;
    font-family: 'Courier';
  }
</style>
<gdwc-menu
  branding="API Driven Menu"
  baseUrl="https://dev-ds-demo.pantheonsite.io"
  menuId="main"
>
  <h1 slot="brand">👾👾👾 Brand Slot 👾👾👾</h1>
</gdwc-menu>
```

### Extending The Component Class

Rather than using the existing styling hooks, it is possible to extend the class
used for any of these components. This approach makes it possible to completely
override portions of a component including the styles() and render() methods.
This allows developers to provide different styling and markup, but use the
existing methods to fetch data and render the levels of the menu. It provides
the greatest flexibility, but also comes with the largest amount of
responsibility.

Example: overriding `styles()` `render()` and exposed template methods defined
in `gdwc-menu`
[(Live example)](/?path=/story/components-menu--class-extend-override)

```js
import { html, css } from 'lit-element';
import { GdwcMenu } from '../../gdwc-menu/gdwc-menu.js';

export class GdwcMenuExtended extends GdwcMenu {
  // Completely overrides shadow DOM styles
  static get styles() {
    return css`
      :host {
        display: block;
        font-family: 'Courier New', Courier, monospace;
      }
      h2 {
        text-align: center;
      }
      ul {
        padding: 0;
        margin: 0;
        background: lightgray;
      }
      li {
        list-style: none;
        padding: 1rem 2rem;
        font-weight: bold;
        cursor: pointer;
      }
      li:hover {
        background-color: green;
      }
      li a {
        color: black;
      }
      ul > li > ul {
        margin-top: 1rem;
      }
      ul > li > ul > li:hover {
        background-color: lightgreen;
      }
    `;
  }

  // Overrides menuItemTemplate to add an emoji to output
  menuItemTemplate = title => {
    return html`<li part="menu-item">🍩 ${title}</li>`;
  };

  render() {
    return html`
      <div class="gdwc-menu-extended">
        <slot name="brand"><h2>${this.branding}</h2></slot>
        <p>
          We're implementing the render method here, and could output whatever
          we want. Or we could continue to use the supporting rendering methods
          on the parent class.
        </p>
        ${this.renderMenuLevel(this.tree)}
      </div>
    `;
  }
}

customElements.get('gdwc-menu-extended') ||
  customElements.define('gdwc-menu-extended', GdwcMenuExtended);
```

## Not Currently Used

### Classes

Another option could be to define a specific set of expected classes that could
be used to impact component styling. This is commonly used for theming for
example.

If our component implemented the following shadow DOM styles:

```js
static get styles() {
  return css`
    :host(.dark) {
      background-color: black;
      color: white;
    }
    :host(.light) {
      background-color: lightgrey;
    }
`;
```

We could enable dark mode by adding the following class to an instance of the
component:

```html
<gdwc-menu
  class="dark"
  branding="API Driven Menu"
  baseUrl="https://dev-ds-demo.pantheonsite.io"
  menuId="main"
>
</gdwc-menu>
```

```

```
